import React, { useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import {
  ErrorMessage,
  MultiSelect,
  SelectDropdown,
  TextInput,
} from '@gsa/afp-component-library';
import DescriptionInput from './description-input';
import StatusInput from './status-input';
import YearInput from './year-input';
import StandardItemCopyInput from './standard-item-copy-input';
import InteractiveCheck from '../InteractiveCheck/InteractiveCheck';
import {
  getRules,
  renderCodeError,
  renderTitleError,
  validate,
} from '../../utilities/validations/error-rules';
import { emDashUnicode } from '../../utilities/constants';

const StandardItemForm = ({
  formId,
  standardItemId,
  handleSave,
  isAddForm,
  fedStandardCodes,
  vehicleGroups,
  vehicleTypes,
  standardItemTags,
  fastCodes,
  years,
  isReadOnly = false, // standardItem?.isStandardItemInOpenSolicitation
  isPartialEditable = false,
  sinSelectedTags = [],
  isSinINBnPeriod,
  isSinInContractYear,
}) => {
  const standardItemCreationYears = years
    ?.filter((year) => Number(year?.value) >= new Date().getFullYear())
    ?.reverse();

  const {
    watch,
    formState: { errors, touchedFields },
    control,
    handleSubmit,
    setValue,
  } = useFormContext();

  const watchContent = watch('content');
  const watchSinYear = isAddForm ? null : watch('year');
  const selectedYear = watch('year');
  const [correspondingVT, setCorrespondingVT] = useState([]);
  const [correspondingFSC, setCorrespondingFSC] = useState([]);

  const onSubmit = (data) => {
    handleSave({ ...data, content: watchContent });
  };

  // code
  const hasCodeError = errors?.standardItemNumber?.message;
  const isDirty = touchedFields?.standardItemNumber || hasCodeError;
  const rules = getRules(watch('standardItemNumber'), 'StandardItemNumber');
  const isValid = validate(rules);
  const canShowCodeError = !isValid && isDirty;

  // standard item Title
  const hasTittleError = errors?.title?.message;
  const isTitleDirty = !isEmpty(watch('title')) || hasTittleError;
  const titleRules = getRules(watch('title'), 'Title');
  const isTitleValid = validate(titleRules);
  const canShowTitleError = !isTitleValid && isTitleDirty && hasTittleError;

  // if form is Edit and isReadOnly is true then disable the inputs
  const disabled = !isAddForm && isReadOnly;
  const currentYear = new Date().getFullYear();
  const isSinInPastYear = !isAddForm && watchSinYear < currentYear;
  const partialEditable = !isAddForm && isPartialEditable; // Edit form and partial editable

  useEffect(() => {
    const VG = vehicleGroups?.find(
      (vg) => vg.value === correspondingVT?.parent,
    );
    const FSC = fedStandardCodes?.find((fsc) => fsc.value === VG?.parent);
    setCorrespondingFSC(FSC);
    setValue('fedStandardCode', FSC?.value);
  }, [correspondingVT]);

  const renderRequiredComponent = (value) => {
    return (
      <h2 className="font-sans-xs" aria-label={value}>
        {value} <span className="usa-hint usa-hint--required">*</span>
      </h2>
    );
  };

  const getTagOptions = () => {
    if (partialEditable || isSinInContractYear) {
      // tags that can be edited on current year
      const editableTags = new Set(['LE', 'NOAREQ', 'PROP', 'REVIEWREQ']);
      return standardItemTags.map((tag) => ({
        ...tag,
        disabled:
          !editableTags.has(tag.value) &&
          (partialEditable || isSinInContractYear),
      }));
    } else if (isSinINBnPeriod) {
      // disabled REQ_CHASSIS_MOD tag if selected
      return standardItemTags.map((tag) => ({
        ...tag,
        disabled:
          tag.value === 'REQ_CHASSIS_MOD' &&
          sinSelectedTags.includes(tag.value),
      }));
    }
    return standardItemTags;
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div
        className={`grid-row padding-bottom-2 ${
          canShowCodeError ? 'padding-left-2' : null
        }`}
      >
        <div className="grid-col-10">
          {renderRequiredComponent('Standard Item Number')}
          <Controller
            name="standardItemNumber"
            control={control}
            render={({ field }) => (
              <TextInput
                id="si_form_standardItemNumber"
                type="text"
                aria-label="standard item number"
                aria-required="true"
                style={{ display: isAddForm ? 'block' : 'none' }}
                {...field}
                errorMessage={renderCodeError(
                  canShowCodeError,
                  'Make sure the Standard Item number meets the requirements listed below.',
                )}
              />
            )}
          />
          {canShowCodeError && (
            <InteractiveCheck checks={rules} isDirty={isDirty} />
          )}
          {!isAddForm && (
            <span className=".text-normal">{watch('standardItemNumber')}</span>
          )}
        </div>
      </div>

      {/* Vehicle Type */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          {renderRequiredComponent('Vehicle type')}
          <Controller
            name="vehicleType"
            control={control}
            render={({ field }) => {
              const VT = vehicleTypes?.find((pvt) => pvt.value === field.value);
              setCorrespondingVT(VT);
              return (
                <SelectDropdown
                  id="si_form_vehicleType_sel"
                  aria-label="vehicle type"
                  onChange={(v) => {
                    field.onChange(v);
                    setCorrespondingVT(
                      vehicleTypes?.find((pvt) => pvt.value === field.value),
                    );
                  }}
                  options={vehicleTypes}
                  value={field.value}
                  onBlur={field.onBlur}
                  disabled={disabled}
                />
              );
            }}
          />
          {errors?.vehicleType?.message && (
            <ErrorMessage>{errors.vehicleType.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* fed std code */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <h2 className="font-sans-xs" aria-label="Federal Standard Code">
            Federal Standard Code
          </h2>
          {correspondingFSC?.label ? (
            <Controller
              name="fedStandardCode"
              defaultValue={correspondingFSC?.value}
              control={control}
              render={({ field }) => (
                <SelectDropdown
                  id="si_form_fedStandardCode_sel"
                  aria-label="federal standard code"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={[correspondingFSC]}
                  value={field.value}
                  disabled={disabled}
                />
              )}
            />
          ) : (
            <Controller
              name="fedStandardCode"
              defaultValue={correspondingFSC?.value}
              control={control}
              render={({ field }) => (
                <SelectDropdown
                  id="si_form_fedStandardCode_sel"
                  aria-label="federal standard code"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={[{ label: 'Select', value: '' }]}
                  value={field.value}
                  disabled={disabled}
                />
              )}
            />
          )}

          {errors?.fedStandardCode?.message && (
            <ErrorMessage>{errors.fedStandardCode.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Contract year */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <YearInput
            name="year"
            isAddForm={isAddForm}
            years={[...standardItemCreationYears]}
            isRequired
            selectedYear={selectedYear}
          />
        </div>
      </div>

      {/* FAST Code Input */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <h2 className="font-sans-xs" aria-label="FAST Code">
            FAST Code
          </h2>

          <Controller
            name="fast"
            control={control}
            render={({ field }) => (
              <SelectDropdown
                id="si_form_fastCode_sel"
                aria-label="fast code"
                onChange={field.onChange}
                options={[
                  ...fastCodes.map((fc) => {
                    if (fc.value === '') {
                      return { label: 'Select', value: '' };
                    }
                    return fc;
                  }),
                ]}
                value={field.value}
                onBlur={field.onBlur}
                disabled={
                  isSinInContractYear ? false : isSinINBnPeriod || disabled
                }
              />
            )}
          />
          {errors?.fast?.message && (
            <ErrorMessage>{errors.fast.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <StatusInput
            disabled={isSinInPastYear}
            standardItemId={standardItemId}
          />
        </div>
      </div>

      <div
        className={`grid-row padding-bottom-2 ${
          hasTittleError ? 'padding-left-2' : null
        }`}
      >
        {/* Title */}
        <div className="grid-col-10">
          {renderRequiredComponent('Title')}
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <TextInput
                id="si_form_title"
                aria-label="title"
                aria-required="true"
                {...field}
                type="text"
                errorMessage={renderTitleError(canShowTitleError)}
              />
            )}
          />
          {hasTittleError && (
            <InteractiveCheck checks={titleRules} isDirty={isTitleDirty} />
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <h2 className="font-sans-xs" aria-label="Tags">
            Tags
          </h2>
          {disabled ? (
            <span className=".text-normal">
              {(sinSelectedTags?.length && sinSelectedTags?.join(', ')) ||
                emDashUnicode}
            </span>
          ) : (
            <>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    data-testid="si_form_tags_sel"
                    key="i_form_tags_sel"
                    name="tags"
                    options={getTagOptions()}
                    value={field.value}
                    onChange={(val, checked) => {
                      let v;
                      if (checked) {
                        v = [...field.value, val];
                      } else {
                        v = field.value.filter((el) => el !== val);
                      }
                      field.onChange(v);
                    }}
                    selectedValues={field.value}
                  />
                )}
              />
              {errors?.tags?.message && (
                <ErrorMessage>{errors.tags.message}</ErrorMessage>
              )}
            </>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-10">
          <DescriptionInput />
        </div>
      </div>

      {isAddForm && (
        <div className="grid-row padding-bottom-2">
          <div className="grid-col-10">
            <StandardItemCopyInput
              isMandatory={false}
              sectionTitle="COPY EQUIPMENT CODE ASSOCIATIONS FROM"
              years={years}
              selectedYear={selectedYear}
              yearInputName="copyFromYear"
              sinInputName="copyFromStandardItemId"
            />
          </div>
        </div>
      )}
    </form>
  );
};

StandardItemForm.propTypes = {
  formId: PropTypes.string.isRequired,
  standardItemId: PropTypes.string,
  handleSave: PropTypes.func.isRequired,
  isAddForm: PropTypes.bool.isRequired,
  fedStandardCodes: PropTypes.shape(Object).isRequired,
  vehicleGroups: PropTypes.arrayOf(Object).isRequired,
  vehicleTypes: PropTypes.shape(Object).isRequired,
  fastCodes: PropTypes.shape(Object).isRequired,
  standardItemTags: PropTypes.shape(Object).isRequired,
  years: PropTypes.arrayOf(Object).isRequired,
  isReadOnly: PropTypes.bool,
  isPartialEditable: PropTypes.bool,
  sinSelectedTags: PropTypes.arrayOf(PropTypes.string),
  isSinINBnPeriod: PropTypes.bool,
  isSinInContractYear: PropTypes.bool,
};

export default StandardItemForm;
